// Repeats, as the kit has them: the rule's days (Models.swift: RepeatRule, MonthPosition), completing a repeating
// to-do and taking it back, skipping a time, and repeating projects (Store.swift: toggleCompletion, spawnNextCopy,
// skippedDay, skipOccurrence, complete, spawnNextProject, copyProject). The kit is the reference.
//
// Everything here is pure: records and a context ("today", now, the zone) in, the records to save or delete out
// (edits.ts). Days are plain dates; a reminder is a moment, which goes off at its time on the to-do's day.
//
// Where the kit reads "today" from the device's clock, these functions take it as `context.today`. The kit's rule
// never returns a day on or before today (except for a monthly place or weekdays when `after` is far ahead, which
// count from `after`), so a to-do completed late comes back in the future, not in the past.

import { stableUUID } from "./birthdays";
import { addDays, addMonths, addYears, at, dayOf, dayParts, daysBetween, maxDay, timeOf, weekday, weekStart } from "./days";
import { makeID, noEdits, type Edits, type RuleContext } from "./edits";
import { updateTodo } from "./actions";
import { orderAfter, orderAtEnd } from "./order";
import type { Project, Section, Todo } from "./records";
import type { Day, MonthPosition, RepeatRule, Start, Weekday } from "./types";

/** "Today" and the first day of the week, which is all the rule itself needs. */
export type DayContext = Pick<RuleContext, "today" | "firstWeekday">;

// MARK: - The rule

/** The place in the month, or undefined when the rule repeats on the day number (only monthly rules use one). */
export function activeMonthPosition(rule: RepeatRule): MonthPosition | undefined {
  return rule.unit === "month" ? rule.monthPosition : undefined;
}

/** The chosen weekdays, or undefined when the rule uses none (only weekly rules with at least one day). */
export function activeWeekdays(rule: RepeatRule): ReadonlySet<Weekday> | undefined {
  return rule.unit === "week" && rule.weekdays !== undefined && rule.weekdays.length > 0 ? new Set(rule.weekdays) : undefined;
}

/**
 * The chosen days of the month (1 … 31), in order and each once, or undefined when the rule uses none (only monthly
 * rules without a place in the month; RepeatRule.activeMonthDays).
 */
export function activeMonthDays(rule: RepeatRule): number[] | undefined {
  if (rule.unit !== "month" || rule.monthPosition !== undefined || rule.monthDays === undefined) return undefined;
  const days = [...new Set(rule.monthDays.filter((day) => Number.isInteger(day) && day >= 1 && day <= 31))].sort((a, b) => a - b);
  return days.length > 0 ? days : undefined;
}

/** The day `number` stands for in the month that holds `month`: itself, or the month's last day when it is shorter. */
export function dayInMonth(number: number, month: Day): Day {
  const start = firstOfMonth(month);
  return addDays(start, Math.min(Math.max(number, 1), monthLength(start)) - 1);
}

/** The first of `days` on or after `onOrAfter`, looking at every `stepping`th month from the month of `origin`. */
function monthDaysDay(days: readonly number[], onOrAfter: Day, stepping: number, origin: Day = onOrAfter): Day | null {
  let month = firstOfMonth(origin);
  for (let index = 0; index < 12 * 50; index++) {
    const candidate = days.map((day) => dayInMonth(day, month)).sort().find((day) => day >= onOrAfter);
    if (candidate !== undefined) return candidate;
    month = addMonths(month, stepping);
  }
  return null;
}

/** The number of days in the day's month. */
function monthLength(day: Day): number {
  const { year, month } = dayParts(day);
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

function firstOfMonth(day: Day): Day {
  return addDays(day, 1 - dayParts(day).date);
}

/** The place in the month around a day; the day of the month itself only as the first or last (MonthPosition.of). */
export function monthPositionOf(day: Day): MonthPosition {
  const date = dayParts(day).date;
  // Within the last seven days it is the last of its weekday; "the fifth Monday" doesn't exist every month.
  return { ordinal: date + 7 > monthLength(day) ? -1 : Math.floor((date - 1) / 7) + 1, weekday: weekday(day) };
}

/** The day a place falls on in the month that holds `month`, or null when there is none (MonthPosition.day(inMonthOf:)). */
export function monthPositionDay(position: MonthPosition, month: Day): Day | null {
  const start = firstOfMonth(month);
  const length = monthLength(start);
  if (position.weekday === undefined) {
    if (position.ordinal === -1) return addDays(start, length - 1);
    return position.ordinal === 1 ? start : null;
  }
  if (position.ordinal === -1) {
    const last = addDays(start, length - 1);
    const back = (weekday(last) - position.weekday + 7) % 7;
    return addDays(last, -back);
  }
  if (position.ordinal < 1 || position.ordinal > 4) return null;
  const ahead = (position.weekday - weekday(start) + 7) % 7;
  return addDays(start, ahead + (position.ordinal - 1) * 7);
}

/** The first day of `position` on or after `day`, looking at every `stepping`th month from the month of `origin`. */
function monthDay(position: MonthPosition, onOrAfter: Day, stepping: number, origin: Day = onOrAfter): Day | null {
  let month = firstOfMonth(origin);
  for (let index = 0; index < 12 * 50; index++) {
    const candidate = monthPositionDay(position, month);
    if (candidate !== null && candidate >= onOrAfter) return candidate;
    month = addMonths(month, stepping);
  }
  return null;
}

/** The first day on or after `day` that the rule falls on (RepeatRule.firstDay). */
export function firstDay(rule: RepeatRule, day: Day): Day {
  const position = activeMonthPosition(rule);
  if (position) return monthDay(position, day, 1) ?? day;
  const monthDays = activeMonthDays(rule);
  if (monthDays) return monthDaysDay(monthDays, day, 1) ?? day;
  const days = activeWeekdays(rule);
  if (!days) return day;
  let candidate = day;
  for (let index = 0; index < 7; index++) {
    if (days.has(weekday(candidate))) return candidate;
    candidate = addDays(candidate, 1);
  }
  return day;
}

/**
 * The next day after `after`, always after today (RepeatRule.next(after:)). Plain rules step from `after` by their
 * interval until they pass today, each step from the last (so a monthly rule from 31 January goes 28 February, then
 * 28 March, as Calendar adds months). Weekdays walk day by day, with an interval only every nth week counted from
 * the week of `after`; a place in the month counts every nth month from the month of `after`.
 */
export function nextDay(rule: RepeatRule, after: Day, context: DayContext): Day {
  const { today } = context;
  const every = Math.max(rule.interval, 1);
  const days = activeWeekdays(rule);
  if (days) {
    const firstWeekday = context.firstWeekday ?? 2;
    const firstWeek = weekStart(after, firstWeekday);
    let day = maxDay(after, today);
    for (let index = 0; index < every * 7 * 53; index++) {
      day = addDays(day, 1);
      if (!days.has(weekday(day))) continue;
      const distance = daysBetween(firstWeek, weekStart(day, firstWeekday)) / 7;
      if (distance % every === 0) return day;
    }
    return day;
  }
  const position = activeMonthPosition(rule);
  if (position) {
    const from = addDays(maxDay(after, today), 1);
    return monthDay(position, from, every, after) ?? from;
  }
  const monthDays = activeMonthDays(rule);
  if (monthDays) {
    // The same with the chosen days: the series keeps them, wherever this one was moved to (docs/67).
    const from = addDays(maxDay(after, today), 1);
    return monthDaysDay(monthDays, from, every, after) ?? from;
  }
  let next = after;
  do {
    switch (rule.unit) {
      case "day": next = addDays(next, every); break;
      case "week": next = addDays(next, 7 * every); break;
      case "month": next = addMonths(next, every); break;
      case "year": next = addYears(next, every); break;
    }
  } while (next <= today);
  return next;
}

/** Whether the series still has `next`: false once it is past its end (RepeatRule.continues(to:)). */
export function continues(rule: RepeatRule, next: Day): boolean {
  return rule.endsOn === undefined || next <= rule.endsOn;
}

/** The day a to-do or project is scheduled for: its start day, or today when it has none. */
export function scheduledDay(start: Start | undefined, today: Day): Day {
  return start?.kind === "on" ? start.day : today;
}

/**
 * The day the next one comes, from completing on `today`: after the scheduled day, or after today when the rule
 * counts from completion; null when the series ends before it (spawnNextCopy, spawnNextProject).
 */
export function nextOccurrence(rule: RepeatRule, scheduled: Day, context: DayContext): Day | null {
  const next = nextDay(rule, rule.afterCompletion ? context.today : scheduled, context);
  return continues(rule, next) ? next : null;
}

/**
 * A to-do that repeats on certain weekdays, days of the month or a place in the month starts on one of them
 * (Todo.alignStartWithRepeat). `origin` is the day to look from instead of the start: the Repeat picker passes the
 * start it opened with, so choosing one day after another looks from there each time.
 */
export function alignStartWithRepeat(todo: Todo, today: Day, origin?: Day): Todo {
  if (!todo.repeats) return todo;
  // QuickParser's ensureDate: a to-do without a day gets today.
  const day = origin ?? (todo.start.kind === "on" ? todo.start.day : today);
  return { ...todo, start: { kind: "on", day: firstDay(todo.repeats, maxDay(day, today)) } };
}

// MARK: - Choosing the days (TodoChoices.swift, docs/67)

/** The day the series' start stands for: the planned day, or today when it has passed or there is none (Todo.repeatStartDay). */
export function repeatStartDay(todo: Pick<Todo, "start">, today: Day): Day {
  return todo.start.kind === "on" ? maxDay(todo.start.day, today) : today;
}

/** The weekdays a weekly repeat shows chosen: its own, or the start's weekday, which it repeats on without them. */
export function shownRepeatWeekdays(todo: Pick<Todo, "start" | "repeats">, today: Day): Weekday[] {
  const own = todo.repeats && activeWeekdays(todo.repeats);
  return own ? [...own].sort((a, b) => a - b) : [weekday(repeatStartDay(todo, today))];
}

/** The days of the month a monthly repeat shows chosen: its own, or the start's day number. */
export function shownRepeatMonthDays(todo: Pick<Todo, "start" | "repeats">, today: Day): number[] {
  return (todo.repeats && activeMonthDays(todo.repeats)) ?? [dayParts(repeatStartDay(todo, today)).date];
}

/** A monthly rule on these days of the month (1 … 31, 31 the last day), or on the start's day number with none. */
export function withMonthDays(rule: RepeatRule, days: readonly number[] | undefined): RepeatRule {
  if (rule.unit !== "month") return rule;
  const result: RepeatRule = { ...rule };
  const kept = [...new Set((days ?? []).filter((day) => Number.isInteger(day) && day >= 1 && day <= 31))].sort((a, b) => a - b);
  if (kept.length > 0) {
    result.monthDays = kept;
    delete result.monthPosition;
  } else {
    delete result.monthDays;
  }
  return result;
}

/**
 * Adds or takes away a day of a monthly repeat (Todo.toggleRepeatMonthDay). While none is stored, the start's day only
 * shows chosen: the first tap chooses the day tapped instead of it.
 */
export function toggledMonthDay(rule: RepeatRule, day: number): RepeatRule {
  const days = new Set(activeMonthDays(rule) ?? []);
  if (days.has(day)) days.delete(day);
  else days.add(day);
  return withMonthDays(rule, [...days]);
}

/** The month (1 … 12) and day a yearly repeat falls on: the start's (Todo.repeatYearDay). */
export function repeatYearDay(todo: Pick<Todo, "start">, today: Day): { month: number; day: number } {
  const { month, date } = dayParts(repeatStartDay(todo, today));
  return { month, day: date };
}

/**
 * The start a yearly repeat gets on another date (Todo.setRepeatYearDay): its next one from `origin` (or the start) and
 * today. The 29th of February falls on the 28th in other years.
 */
export function yearDayStart(todo: Pick<Todo, "start">, month: number, day: number, today: Day, origin?: Day): Day {
  const from = maxDay(origin ?? repeatStartDay(todo, today), today);
  let year = dayParts(from).year;
  for (let index = 0; index < 2; index++) {
    const first = `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-01` as Day;
    const candidate = dayInMonth(day, first);
    if (candidate >= from) return candidate;
    year += 1;
  }
  return from;
}

/** The day a monthly repeat's place in the month would start from: the planned day, or today. */
export function monthPositionSuggestion(todo: Todo, today: Day): MonthPosition {
  return monthPositionOf(todo.start.kind === "on" ? maxDay(todo.start.day, today) : today);
}

// MARK: - Reminders

/** A zone the browser knows, or undefined (an unknown name is read as if there were none, as the kit does). */
export function knownZone(zone: string | undefined): string | undefined {
  if (zone === undefined) return undefined;
  try {
    new Intl.DateTimeFormat("en", { timeZone: zone });
    return zone;
  } catch {
    return undefined;
  }
}

/**
 * The reminder's time on the clock (Todo.reminderClock): its hour and minute where it was set (`reminderZone`), so
 * 9:00 set in Berlin is 9:00 in New York too (docs/48). Without a zone (set before 1.1), read in `zone`, the browser's.
 */
export function reminderClock(todo: Pick<Todo, "reminder" | "reminderZone">, zone: string): { hour: number; minute: number } | undefined {
  return todo.reminder && clockOf(todo.reminder, knownZone(todo.reminderZone) ?? zone);
}

/** The same for the series' reminder, which every next one of a repeat gets (Todo.seriesReminderClock). */
export function seriesReminderClock(todo: Pick<Todo, "seriesReminder" | "seriesReminderZone">, zone: string): { hour: number; minute: number } | undefined {
  return todo.seriesReminder && clockOf(todo.seriesReminder, knownZone(todo.seriesReminderZone) ?? zone);
}

// MARK: - Setting a reminder

/** The day of a reminder: the to-do's day, or today when it is overdue or has none (Todo.reminderDay). */
export function reminderDay(todo: Pick<Todo, "start">, today: Day): Day {
  return todo.start.kind === "on" && todo.start.day > today ? todo.start.day : today;
}

/**
 * Whether setting a reminder brings the to-do to Today (Todo.reminderMovesToToday): one without a day or whose day has
 * passed. The picker says so under its times (Bastian, 2026-10-20).
 */
export function reminderMovesToToday(todo: Pick<Todo, "start">, today: Day): boolean {
  return !(todo.start.kind === "on" && todo.start.day === reminderDay(todo, today));
}

/**
 * Whether a reminder at this time on the to-do's day would already have gone off (Todo.reminderHasPassed): the picker
 * dims it and refuses it typed. Only a time for this one alone can have: the series' time (Future Ones, and the first
 * reminder of a repeating to-do) is for the next ones, all to come (docs/62).
 */
export function reminderHasPassed(todo: Pick<Todo, "start" | "repeats" | "habitID" | "reminder" | "seriesReminder">, hour: number, minute: number, today: Day, zone: string, scope: ReminderScope = "this", now: Date = new Date()): boolean {
  return !setsSeriesReminder(todo, scope) && at(reminderDay(todo, today), hour, minute, zone) <= now;
}

/**
 * A reminder at this time on the to-do's day (Todo.setReminder): one without a day, or overdue, comes to today with
 * it. Mutates.
 */
export function setReminder(todo: Todo, hour: number, minute: number, today: Day, zone: string): void {
  const day = reminderDay(todo, today);
  if (!(todo.start.kind === "on" && todo.start.day === day)) todo.start = { kind: "on", day };
  todo.reminder = at(day, hour, minute, zone);
  // Set here, so it reads right at once (docs/48; the kit's Todo.setReminder): with the zone it had, a time set in
  // Lisbon on a to-do from Berlin read an hour ahead until the store noted the zone.
  todo.reminderZone = zone;
}

/**
 * Which reminder of a repeating to-do a change in the reminder picker is for (docs/62; TodoChoices.swift:
 * ReminderScope): this one's, or the one every next one gets (the series' time). Each changes only the time it shows
 * (Bastian, 2026-10-21). The picker shows the two as tabs, This One first, for a to-do that repeats and reminds; for one
 * that doesn't repeat, both are this one. `cases/reminders.json` checks that the kit, this and the server agree.
 */
export type ReminderScope = "this" | "future";

/**
 * One of a series: a to-do that repeats, or a habit's day, which carries the habit's time as its series' time
 * (Todo.isOfASeries; docs/71, Bastian 2026-11-03: "Shouldn't a habit basically behave like a series?").
 */
export function isOfASeries(todo: Pick<Todo, "repeats" | "habitID">): boolean {
  return todo.repeats !== undefined || todo.habitID !== undefined;
}

/**
 * Whether the reminder picker asks This One or Future Ones: only a to-do that repeats has future ones, and only once it
 * reminds (this one or the series). The first reminder of a routine is the routine's.
 */
export function offersReminderScope(todo: Pick<Todo, "repeats" | "habitID" | "reminder" | "seriesReminder">): boolean {
  return isOfASeries(todo) && (todo.reminder !== undefined || todo.seriesReminder !== undefined);
}

/** Whether a choice for a scope sets this one's reminder: This One, a to-do that doesn't repeat, and the first reminder of a repeating one. */
export function setsThisReminder(todo: Pick<Todo, "repeats" | "habitID" | "reminder" | "seriesReminder">, scope: ReminderScope): boolean {
  return scope === "this" || !offersReminderScope(todo);
}

/** Whether it sets the series' time: Future Ones, and the first reminder of a repeating to-do. Then any time may be taken. */
export function setsSeriesReminder(todo: Pick<Todo, "repeats" | "habitID" | "reminder" | "seriesReminder">, scope: ReminderScope): boolean {
  return isOfASeries(todo) && (scope === "future" || !offersReminderScope(todo));
}

/**
 * Whether setting a reminder for a scope brings the to-do to Today (Todo.reminderMovesToToday(for:)): only this one's
 * reminder hangs on its day; the series' time leaves this one where it is.
 */
export function reminderMovesToTodayFor(todo: Pick<Todo, "start" | "repeats" | "habitID" | "reminder" | "seriesReminder">, scope: ReminderScope, today: Day): boolean {
  return setsThisReminder(todo, scope) && reminderMovesToToday(todo, today);
}

/**
 * The time a scope's reminder is at (Todo.reminderClock(for:)): this one's, or the one every next one gets (the
 * series'; older data has none, then this one's, as `nextCopy` reads it).
 */
export function reminderClockFor(todo: Pick<Todo, "repeats" | "habitID" | "reminder" | "reminderZone" | "seriesReminder" | "seriesReminderZone">, scope: ReminderScope, zone: string): { hour: number; minute: number } | undefined {
  if (scope === "this" || !isOfASeries(todo)) return reminderClock(todo, zone);
  return seriesReminderClock(todo, zone) ?? reminderClock(todo, zone);
}

/**
 * A reminder at this time for a scope (Todo.setReminder(hour:minute:for:)), the one call the picker makes: This One
 * sets this one's (`setReminder`), and the next ones keep the series' time; Future Ones sets the series' time, and this
 * one keeps its own. The first reminder of a repeating to-do is both, whichever scope asks. Mutates.
 */
export function setScopedReminder(todo: Todo, hour: number, minute: number, scope: ReminderScope, today: Day, zone: string): void {
  const series = setsSeriesReminder(todo, scope);
  if (setsThisReminder(todo, scope)) {
    keepSeriesReminder(todo, scope);
    setReminder(todo, hour, minute, today, zone);
    if (!series) return;
    todo.seriesReminder = todo.reminder;
    todo.seriesReminderZone = zone;
  } else if (series) {
    // On this one's day, which stays as it is; the next ones take its time on the clock, in the zone it was set in.
    todo.seriesReminder = at(reminderDay(todo, today), hour, minute, zone);
    todo.seriesReminderZone = zone;
  }
}

/**
 * Removes a scope's reminder (Todo.removeReminder(for:)): under This One this one's only, and the next ones still
 * remind; under Future Ones the series' and this one's too: stored data can't say "none from now on, but this one",
 * and the next one would take this one's time. Mutates.
 */
export function removeScopedReminder(todo: Todo, scope: ReminderScope): void {
  keepSeriesReminder(todo, scope);
  delete todo.reminder;
  delete todo.reminderZone;
  if (scope !== "future" || !isOfASeries(todo)) return;
  delete todo.seriesReminder;
  delete todo.seriesReminderZone;
}

/**
 * Under This One, a series without a time of its own (older data, whose next one takes this one's) keeps the time it
 * had, so a change to this one stays with this one.
 */
function keepSeriesReminder(todo: Todo, scope: ReminderScope): void {
  if (scope !== "this" || !isOfASeries(todo) || todo.seriesReminder !== undefined || todo.reminder === undefined) return;
  todo.seriesReminder = todo.reminder;
  if (todo.reminderZone === undefined) delete todo.seriesReminderZone;
  else todo.seriesReminderZone = todo.reminderZone;
}

function clockOf(moment: Date, zone: string): { hour: number; minute: number } {
  const [hour = 9, minute = 0] = timeOf(moment, zone).split(":").map(Number);
  return { hour, minute };
}

/**
 * When a to-do's reminder goes off where the browser is (ReminderPlan.date(of:)): its time on the clock
 * (`reminderClock`) on the to-do's day, so it follows the to-do to another day and keeps its time wherever you are;
 * without a day, on the date it had where it was set. Everything that shows a reminder's time uses this.
 */
export function reminderMoment(todo: Pick<Todo, "reminder" | "reminderZone" | "start">, zone: string): Date | undefined {
  if (!todo.reminder) return undefined;
  const there = knownZone(todo.reminderZone) ?? zone;
  const { hour, minute } = clockOf(todo.reminder, there);
  const day = todo.start.kind === "on" ? todo.start.day : dayOf(todo.reminder, there);
  return at(day, hour, minute, zone);
}

/** A moment `count` days later on the clock of the zone, as Calendar adds days (the same time of day). */
export function addDaysToMoment(moment: Date, count: number, zone: string): Date {
  if (count === 0) return moment;
  const day = dayOf(moment, zone);
  const [hour, minute] = timeOf(moment, zone).split(":").map(Number);
  const seconds = moment.getTime() - at(day, hour ?? 0, minute ?? 0, zone).getTime();
  return new Date(at(addDays(day, count), hour ?? 0, minute ?? 0, zone).getTime() + seconds);
}

// MARK: - Completing a to-do

/**
 * The ids of the next copies a completion makes (Models.swift: NextCopyID, docs/69): the same on every device, in the
 * kit and in the MCP server (records.nextCopyID), so that two writers of one completion write one document instead of
 * two. A version 5 UUID of a prefix and the completed one's id (stableUUID). Cases: `nextCopyIDs` in cases/repeats.json.
 */
export function nextCopyID(completedTodoID: string): string {
  return stableUUID(`next:${completedTodoID.toUpperCase()}`);
}

/** The next one of the repeating project with this id (NextCopyID.project). */
export function nextProjectID(completedProjectID: string): string {
  return stableUUID(`next-project:${completedProjectID.toUpperCase()}`);
}

/** A section or to-do of that copy, from the one it was copied from (NextCopyID.record). */
export function nextProjectRecordID(originalID: string, copyID: string): string {
  return stableUUID(`next-project:${copyID.toUpperCase()}:${originalID.toUpperCase()}`);
}

/**
 * The next one of a completed repeating to-do, on its next day (Store.spawnNextCopy), or null when it doesn't repeat
 * or its series ends. Everything stays as it was, except: a new id, open, made now, no calendar event, the
 * occurrence it came from (`spawnedFrom`), the next day, the series' reminder (not one changed for this occurrence
 * only; older data has none, then this one's), the checklist unticked with new ids, no plan for today, and the
 * deadline as far from the day as it was. This also works ahead of schedule. Its id follows from the completed one's
 * (nextCopyID).
 */
export function nextCopy(todo: Todo, context: RuleContext): Todo | null {
  const rule = todo.repeats;
  if (!rule) return null;
  const scheduled = scheduledDay(todo.start, context.today);
  const next = nextOccurrence(rule, scheduled, context);
  if (next === null) return null;
  const copy: Todo = { ...todo, id: nextCopyID(todo.id), createdAt: context.now, spawnedFrom: todo.id, start: { kind: "on", day: next } };
  delete copy.completedAt;
  delete copy.completedBy;
  delete copy.calendarEventID;
  delete copy.plannedUntil;
  delete copy.planOrder;
  const reminder = todo.seriesReminder ?? todo.reminder;
  // The series' zone with the series' reminder (Store.spawnNextCopy).
  const zone = todo.seriesReminder === undefined ? todo.reminderZone : todo.seriesReminderZone;
  delete copy.reminderZone;
  delete copy.seriesReminderZone;
  if (reminder === undefined) {
    delete copy.reminder;
    delete copy.seriesReminder;
  } else {
    copy.reminder = reminder;
    copy.seriesReminder = reminder;
    if (zone !== undefined) {
      copy.reminderZone = zone;
      copy.seriesReminderZone = zone;
    }
  }
  copy.checklist = todo.checklist.map((item) => ({ id: makeID(context), title: item.title, isDone: false }));
  if (todo.deadline !== undefined) copy.deadline = addDays(next, daysBetween(scheduled, todo.deadline));
  return copy;
}

/**
 * Ticking a to-do off or open again (Store.toggleCompletion). Completed: `completedAt` now, who did it when it is in
 * a share (`completedBy`, the person's uid, given only then), and the next one of a repeating to-do. Open again:
 * both cleared, and the next one its completion made goes again while it is still open, the one that says it came
 * from this (`spawnedFrom`), also after a restart or when it was made on another device. One already completed stays.
 * One completion makes one copy (docs/69): none when one is here already, the one with its id (also when done) or an
 * open one that says it came from this (made by an older version, with an id of its own).
 */
export function toggleCompletion(todo: Todo, todos: readonly Todo[], context: RuleContext & { completedBy?: string }): Edits {
  const edits = noEdits();
  if (todo.completedAt === undefined) {
    const completed: Todo = { ...todo, completedAt: context.now };
    if (context.completedBy !== undefined) completed.completedBy = context.completedBy;
    edits.todos.saved.push(completed);
    const copyID = nextCopyID(todo.id);
    const made = todos.some((other) => other.id === copyID || (other.spawnedFrom === todo.id && other.completedAt === undefined));
    const copy = made ? null : nextCopy(completed, context);
    if (copy) edits.todos.saved.push(copy);
  } else {
    const reopened: Todo = { ...todo };
    delete reopened.completedAt;
    delete reopened.completedBy;
    edits.todos.saved.push(reopened);
    edits.todos.deleted = todos.filter((other) => other.spawnedFrom === todo.id && other.completedAt === undefined).map((other) => other.id);
  }
  return edits;
}

// MARK: - Skipping a time

/** The day a repeating to-do would move to when skipped: its next one, if the series goes on that far (Store.skippedDay). */
export function skippedDay(todo: Todo, context: DayContext): Day | null {
  const rule = todo.repeats;
  if (todo.completedAt !== undefined || !rule) return null;
  const scheduled = scheduledDay(todo.start, context.today);
  const next = nextDay(rule, rule.afterCompletion ? maxDay(scheduled, context.today) : scheduled, context);
  return continues(rule, next) ? next : null;
}

/**
 * Skips this time of a repeating to-do (Store.skipOccurrence): it moves to its next day without being completed, its
 * deadline and reminder move by as many days, today's plan goes. Null when it can't be skipped. Saved as an edit
 * (`updateTodo`), so leaving the Inbox is noted as for any change.
 */
export function skipOccurrence(todo: Todo, context: RuleContext): Edits | null {
  const next = skippedDay(todo, context);
  if (next === null) return null;
  const days = daysBetween(scheduledDay(todo.start, context.today), next);
  const skipped: Todo = { ...todo, start: { kind: "on", day: next } };
  if (todo.deadline !== undefined) skipped.deadline = addDays(todo.deadline, days);
  if (todo.reminder !== undefined) skipped.reminder = addDaysToMoment(todo.reminder, days, context.zone);
  delete skipped.plannedUntil;
  delete skipped.planOrder;
  return updateTodo(todo, skipped, context.now);
}

// MARK: - Projects

/** What copying a project needs to see: every project, section and to-do (for the orders and the project's own). */
export interface ProjectData {
  projects: readonly Project[];
  sections: readonly Section[];
  todos: readonly Todo[];
}

export interface ProjectCopy {
  project: Project;
  sections: Section[];
  todos: Todo[];
}

/**
 * A copy of a project with its sections and to-dos (Store.copyProject). The copy goes right after the original; its
 * to-dos are new records, open, at the end, with their checklists unticked; what was completed, calendar links,
 * today's plans and samples stay behind. `shift`: days that dated to-dos, their reminders and the deadlines move by
 * (a repeating project's next time). `start`: the copy's start, when it changes.
 */
export function copyProject(
  original: Project, data: ProjectData, context: RuleContext, options: { includesCompleted: boolean; start?: Start; shift?: number; id?: string },
): ProjectCopy {
  const shift = options.shift ?? 0;
  // A repeating project's next time has its id given (nextProjectID); its sections and to-dos follow from it.
  const copyID = options.id;
  const recordID = (old: string) => (copyID === undefined ? makeID(context) : nextProjectRecordID(old, copyID));
  const project: Project = { ...original, id: copyID ?? makeID(context) };
  delete project.completedAt;
  delete project.isSample;
  // A copy is no run of the template: its to-dos are new records.
  delete project.templateID;
  delete project.templateSteps;
  if (options.start !== undefined) project.start = options.start;
  if (original.deadline !== undefined) project.deadline = addDays(original.deadline, shift);
  project.order = orderAfter(original.order, data.projects.map((other) => other.order));

  const sectionIDs = new Map<string, string>();
  const sections = data.sections.filter((section) => section.projectID === original.id).map((section) => {
    const copy: Section = { ...section, id: recordID(section.id), projectID: project.id };
    delete copy.isSample;
    sectionIDs.set(section.id, copy.id);
    return copy;
  });

  const firstOrder = orderAtEnd(data.todos.map((todo) => todo.order));
  const taken = data.todos
    .filter((todo) => todo.projectID === original.id && (options.includesCompleted || todo.completedAt === undefined))
    .sort((a, b) => a.order - b.order);
  const todos = taken.map((todo, place) => {
    const copy: Todo = { ...todo, id: recordID(todo.id), projectID: project.id, createdAt: context.now, order: firstOrder + place };
    const section = todo.sectionID === undefined ? undefined : sectionIDs.get(todo.sectionID);
    if (section === undefined) delete copy.sectionID;
    else copy.sectionID = section;
    for (const key of ["todaySectionID", "completedAt", "completedBy", "calendarEventID", "plannedUntil", "planOrder", "isSample", "keptAt"] as const) {
      delete copy[key];
    }
    copy.checklist = todo.checklist.map((item) => ({ id: makeID(context), title: item.title, isDone: false }));
    if (shift !== 0) {
      if (todo.start.kind === "on") copy.start = { kind: "on", day: addDays(todo.start.day, shift) };
      if (todo.deadline !== undefined) copy.deadline = addDays(todo.deadline, shift);
      if (todo.reminder !== undefined) copy.reminder = addDaysToMoment(todo.reminder, shift, context.zone);
    }
    return copy;
  });
  return { project, sections, todos };
}

/** Duplicating a project (Store.duplicateProject): its open to-dos come along; the start and the days stay. */
export function duplicateProject(original: Project, data: ProjectData, context: RuleContext): ProjectCopy {
  return copyProject(original, data, context, { includesCompleted: false });
}

/**
 * The next one of a completed repeating project (Store.spawnNextProject): a fresh copy, all its to-dos open again,
 * waiting for the next day of its rule, its days moved by as many days as the project's; null when it doesn't
 * repeat or its series has ended. `data` holds the project's to-dos as they are after completing it.
 */
export function nextProject(project: Project, data: ProjectData, context: RuleContext): ProjectCopy | null {
  const rule = project.repeats;
  if (!rule) return null;
  const scheduled = scheduledDay(project.start, context.today);
  const next = nextOccurrence(rule, scheduled, context);
  if (next === null) return null;
  const id = nextProjectID(project.id);
  // One completion makes one copy (Store.spawnNextProject, docs/69): not again when it is here already.
  if (data.projects.some((other) => other.id === id)) return null;
  return copyProject(project, data, context, { includesCompleted: true, start: { kind: "on", day: next }, shift: daysBetween(scheduled, next), id });
}

/**
 * Completes a project with everything still open in it (Store.complete). Its to-dos are completed as they are (a
 * repeating one makes no copy of its own); a repeating project comes back as its next copy.
 */
export function completeProject(project: Project, data: ProjectData, context: RuleContext): Edits {
  const edits = noEdits();
  const completed: Project = { ...project, completedAt: context.now };
  edits.projects.saved.push(completed);
  const todos = data.todos.map((todo) =>
    todo.projectID === project.id && todo.completedAt === undefined ? { ...todo, completedAt: context.now } : todo);
  edits.todos.saved.push(...todos.filter((todo, index) => todo !== data.todos[index]));
  if (project.repeats) {
    const projects = data.projects.map((other) => (other.id === project.id ? completed : other));
    const copy = nextProject(completed, { projects, sections: data.sections, todos }, context);
    if (copy) {
      edits.projects.saved.push(copy.project);
      edits.sections.saved.push(...copy.sections);
      edits.todos.saved.push(...copy.todos);
    }
  }
  return edits;
}
